import type { AvailablePaymentMethod } from './PaymentMethodChanger';

/**
 * Default sort of payment-method tiles by the admin-configured `Order`
 * (panels "Arrange & label"). Graduated from the `order-present-payment-methods`
 * A/B (NPP-8216). Markets without any `Order` configured keep the backend order.
 */

/**
 * Sorts unavailable-last (e.g. Apple Pay off non-Apple), then methods with a
 * numeric `order` before those without, ascending by `order`, stable for ties.
 * Pure; always returns a new array.
 */
export function orderPaymentMethods(methods: AvailablePaymentMethod[]): AvailablePaymentMethod[] {
  return methods
    .map((m, index) => ({ m, index }))
    .sort((a, b) => {
      const aAvail = a.m.isAvailable ?? true;
      const bAvail = b.m.isAvailable ?? true;
      if (aAvail !== bAvail) {
        return aAvail ? -1 : 1;
      }

      const aHas = typeof a.m.order === 'number';
      const bHas = typeof b.m.order === 'number';
      if (aHas !== bHas) {
        return aHas ? -1 : 1;
      }

      if (aHas && bHas && a.m.order !== b.m.order) {
        return (a.m.order as number) - (b.m.order as number);
      }

      return a.index - b.index;
    })
    .map((x) => x.m);
}

/**
 * Auto-preselect: the order===1 method, but only if available on the device.
 * Returns undefined when no order===1 exists or when it is unavailable —
 * auto-navigating there would dead-end the user on its input form (the
 * changer's onSelect guard does not run for auto-preselect). Backend uniqueness
 * keeps order===1 singular; defensively picks the first available match.
 */
export function pickPreselection(
  methods: AvailablePaymentMethod[]
): AvailablePaymentMethod | undefined {
  return methods.find((m) => m.order === 1 && (m.isAvailable ?? true));
}
